import { useRouter } from 'next/router';

import { useEffect, useState } from 'react';

import { addDays } from 'date-fns/addDays';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import isNil from 'lodash/isNil';

import { LocalStorageKey } from '@nocowanie/core/enums';
import {
    assignDefaultValuesIfNeeded,
    SearchFormDataType,
} from '@nocowanie/common-ui/components/organisms/search-engine-container';

import { SearchCriteriaKeys } from '@app/enums';

export const useDefaultSearchFormData = (initialSearchData?: SearchFormDataType) => {
    const { pathname } = useRouter();
    const isHomePage = pathname === '/';
    const [initialSearchFormData, setInitialSearchFormData] = useState({
        ...assignDefaultValuesIfNeeded({
            ...initialSearchData,
        }),
    });

    useEffect(() => {
        if (
            !isHomePage ||
            typeof localStorage === 'undefined' ||
            isNil(localStorage.getItem(LocalStorageKey.SearchCriteria))
        ) {
            return;
        }

        const lsSearchCriteria = localStorage.getItem(LocalStorageKey.SearchCriteria) as string;
        const decodedSearchString = decodeURIComponent(lsSearchCriteria);
        const params = new URLSearchParams(decodedSearchString);

        const checkInStr = params.get(SearchCriteriaKeys.CheckIn);
        const checkOutStr = params.get(SearchCriteriaKeys.CheckOut);
        const adultsStr = params.get(SearchCriteriaKeys.Adults);
        const childrenStr = params.get(SearchCriteriaKeys.Children);
        const childrenAgesStr = params.get(SearchCriteriaKeys.ChildrenAges);
        const onlineReservation = params.get(SearchCriteriaKeys.OnlineReservation);
        const flexDates = params.get(SearchCriteriaKeys.FlexDates);

        const defaultData = assignDefaultValuesIfNeeded({ ...initialSearchData });

        const today = startOfDay(new Date());
        const checkInDate = checkInStr
            ? startOfDay(new Date(checkInStr))
            : defaultData.dates.checkInDate;
        const checkOutDate = checkOutStr
            ? startOfDay(new Date(checkOutStr))
            : defaultData.dates.checkOutDate;

        const validCheckInDate =
            checkInDate && isBefore(checkInDate, today)
                ? defaultData.dates.checkInDate
                : checkInDate;
        const validCheckOutDate =
            checkOutDate && validCheckInDate && isBefore(checkOutDate, addDays(validCheckInDate, 1))
                ? defaultData.dates.checkOutDate
                : checkOutDate;

        const adultsCount = adultsStr ? parseInt(adultsStr, 10) : defaultData.guests.adultsCount;
        const childrenCount = childrenStr
            ? parseInt(childrenStr, 10)
            : defaultData.guests.childrenCount;
        const childrenAges =
            childrenAgesStr && childrenAgesStr.trim().length > 0
                ? childrenAgesStr
                      .split(',')
                      .map(age => parseInt(age, 10))
                      .filter(age => !isNaN(age))
                : [];

        setInitialSearchFormData({
            location: '',
            dates: {
                checkInDate: validCheckInDate,
                checkOutDate: validCheckOutDate,
            },
            guests: {
                adultsCount,
                childrenCount,
                childrenAges,
            },
            instantReservation: onlineReservation ? !!+onlineReservation : undefined,
            flexDates: flexDates?.length ? parseInt(flexDates) : undefined,
        });
    }, [initialSearchData, isHomePage]);

    return initialSearchFormData;
};
